import type { FeatureNames } from "./feature-names";

/**
 * The French name of every staff feature, looked up by its settingId (the
 * settings carry no names: see shared/src/settings/types.ts), and of every
 * settings category. A new feature or category without a name is a compile
 * error, here and in every other language.
 */
export const featuresFr: FeatureNames = {
  features: {
    SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT: "Rechercher des comptes",
    VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT: "Voir un compte",
    REVEAL_CONTACT_DETAILS_STAFF_FEATURE_ENABLEMENT: "Afficher un numéro",
    EDIT_CUSTOMER_SETTINGS_STAFF_FEATURE_ENABLEMENT: "Modifier les réglages d'un client",
    CLAIM_CASES_STAFF_FEATURE_ENABLEMENT: "Prendre des dossiers",
    REASSIGN_CASES_STAFF_FEATURE_ENABLEMENT: "Réattribuer des dossiers",
    ACT_ON_BEHALF_STAFF_FEATURE_ENABLEMENT: "Agir pour le compte d'un client",
    CORRECT_ACCOUNT_DETAILS_STAFF_FEATURE_ENABLEMENT: "Corriger les informations d'un compte",
    REVIEW_VERIFICATIONS_STAFF_FEATURE_ENABLEMENT: "Examiner les vérifications",
    VIEW_ID_IMAGES_STAFF_FEATURE_ENABLEMENT: "Voir les images d'identité",
    APPROVE_PROS_STAFF_FEATURE_ENABLEMENT: "Approuver les pros",
    VIEW_PAYMENTS_STAFF_FEATURE_ENABLEMENT: "Voir les paiements",
    ISSUE_REFUNDS_STAFF_FEATURE_ENABLEMENT: "Rembourser",
    ISSUE_CREDITS_STAFF_FEATURE_ENABLEMENT: "Accorder un avoir",
    WAIVE_FEES_STAFF_FEATURE_ENABLEMENT: "Annuler des frais",
    MARK_PAYMENTS_PAID_STAFF_FEATURE_ENABLEMENT: "Marquer un paiement payé",
    MANAGE_PLAN_PRICES_STAFF_FEATURE_ENABLEMENT: "Fixer les prix des forfaits",
    RUN_PROMOS_STAFF_FEATURE_ENABLEMENT: "Lancer des promos",
    OFFER_DISCOUNTS_STAFF_FEATURE_ENABLEMENT: "Accorder une remise",
    MANAGE_SUBSCRIPTIONS_STAFF_FEATURE_ENABLEMENT: "Prolonger ou terminer un abonnement",
    APPROVE_PAYMENT_REQUESTS_STAFF_FEATURE_ENABLEMENT: "Accorder au-delà de la limite",
    RECONCILE_PAYMENTS_STAFF_FEATURE_ENABLEMENT: "Rapprocher les paiements",
    EXPORT_PAYMENTS_STAFF_FEATURE_ENABLEMENT: "Exporter",
    HANDLE_REPORTS_STAFF_FEATURE_ENABLEMENT: "Traiter les signalements",
    SET_REPORT_SEVERITY_STAFF_FEATURE_ENABLEMENT: "Fixer la gravité d'un signalement",
    SUSPEND_PENDING_INVESTIGATION_STAFF_FEATURE_ENABLEMENT: "Suspension provisoire",
    ISSUE_VERDICTS_STAFF_FEATURE_ENABLEMENT: "Rendre un verdict",
    BAN_PERMANENTLY_STAFF_FEATURE_ENABLEMENT: "Bannir définitivement",
    HANDLE_APPEALS_STAFF_FEATURE_ENABLEMENT: "Traiter les recours",
    REFER_TO_POLICE_STAFF_FEATURE_ENABLEMENT: "Transmettre à la police",
    SHARE_DATA_WITH_POLICE_STAFF_FEATURE_ENABLEMENT: "Partager des données avec la police",
    PLACE_LEGAL_HOLDS_STAFF_FEATURE_ENABLEMENT: "Geler des preuves",
    MODERATE_REVIEWS_STAFF_FEATURE_ENABLEMENT: "Modérer les avis",
    SEND_SMS_STAFF_FEATURE_ENABLEMENT: "Envoyer un SMS",
    SEND_BULK_SMS_STAFF_FEATURE_ENABLEMENT: "Envoyer des SMS groupés",
    EDIT_SMS_TEMPLATES_STAFF_FEATURE_ENABLEMENT: "Modifier les modèles de SMS",
    MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT: "Gérer les fonctionnalités de l'app",
    EDIT_RULES_STAFF_FEATURE_ENABLEMENT: "Modifier les règles",
    EDIT_PRICING_STAFF_FEATURE_ENABLEMENT: "Modifier les tarifs",
    EDIT_CATEGORIES_STAFF_FEATURE_ENABLEMENT: "Modifier les métiers",
    INVITE_STAFF_STAFF_FEATURE_ENABLEMENT: "Inviter dans l'équipe",
    DEACTIVATE_STAFF_STAFF_FEATURE_ENABLEMENT: "Désactiver un membre",
    END_STAFF_SESSIONS_STAFF_FEATURE_ENABLEMENT: "Couper les sessions",
    MANAGE_TEAMS_STAFF_FEATURE_ENABLEMENT: "Gérer les équipes",
    MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT: "Gérer les rôles",
    MANAGE_ACTION_ENABLEMENTS_STAFF_FEATURE_ENABLEMENT: "Gérer les activations d'actions",
    ASSIGN_ROLES_STAFF_FEATURE_ENABLEMENT: "Attribuer des rôles",
    MANAGE_LEADS_STAFF_FEATURE_ENABLEMENT: "Ventes et croissance",
    VIEW_MARKETPLACE_STAFF_FEATURE_ENABLEMENT: "Opérations marché",
    MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT: "Ressources humaines",
    VIEW_AUDIT_LOG_STAFF_FEATURE_ENABLEMENT: "Lire le journal d'audit",
    VIEW_SENSITIVE_AUDIT_VALUES_STAFF_FEATURE_ENABLEMENT: "Voir les valeurs sensibles",
    VIEW_HEALTH_DASHBOARD_STAFF_FEATURE_ENABLEMENT: "Voir le tableau de bord",
  },
  /** The permissions that sit on those features (ADR-0019), by settingId. */
  permissions: {
    ISSUE_REFUND_STAFF_PERMISSION: "Émettre un remboursement",
    SETTLE_REFUND_STAFF_PERMISSION: "Régler un remboursement",
    ISSUE_CREDIT_STAFF_PERMISSION: "Émettre un crédit",
    WAIVE_FEE_STAFF_PERMISSION: "Annuler des frais",
    REPLY_TO_CUSTOMER_STAFF_PERMISSION: "Répondre à un client",
    CLOSE_CASE_STAFF_PERMISSION: "Clore un dossier",
  },
  /** Customer features (Fonctionnalités), by settingId. */
  customerFeatures: {
    SEARCH_PROS_USER_FEATURE_ENABLEMENT: "Rechercher des pros",
    NEARBY_PROS_USER_FEATURE_ENABLEMENT: "Pros à proximité",
    REQUEST_PRO_USER_FEATURE_ENABLEMENT: "Demander un pro",
    POST_OPEN_JOB_USER_FEATURE_ENABLEMENT: "Publier une demande ouverte",
    JOB_PHOTOS_USER_FEATURE_ENABLEMENT: "Photos dans une demande",
    SELECT_QUOTE_USER_FEATURE_ENABLEMENT: "Choisir un devis",
    LEAVE_REVIEWS_USER_FEATURE_ENABLEMENT: "Laisser un avis",
    REQUEST_CALLBACK_USER_FEATURE_ENABLEMENT: "Demander un rappel",
    SAVE_FAVORITE_PROS_USER_FEATURE_ENABLEMENT: "Pros favoris",
    REBOOK_PRO_USER_FEATURE_ENABLEMENT: "Refaire appel à un pro",
    SHARE_PRO_PROFILE_USER_FEATURE_ENABLEMENT: "Partager le profil d'un pro",
    SCHEDULE_JOB_USER_FEATURE_ENABLEMENT: "Planifier une demande",
    URGENT_JOB_USER_FEATURE_ENABLEMENT: "Demande urgente",
    BECOME_PRO_USER_FEATURE_ENABLEMENT: "Devenir pro",
    LEAD_PREFERENCES_USER_FEATURE_ENABLEMENT: "Préférences d'opportunités",
    EDIT_RATES_USER_FEATURE_ENABLEMENT: "Modifier ses tarifs",
    BROWSE_OPEN_JOBS_USER_FEATURE_ENABLEMENT: "Parcourir les demandes ouvertes",
    QUOTE_JOBS_USER_FEATURE_ENABLEMENT: "Devis sur les demandes",
    PRO_SUBSCRIPTIONS_USER_FEATURE_ENABLEMENT: "Abonnements pro",
    SUBMIT_VERIFICATION_USER_FEATURE_ENABLEMENT: "Envoyer sa vérification",
    PAUSE_LEADS_USER_FEATURE_ENABLEMENT: "Mettre les opportunités en pause",
    PORTFOLIO_PHOTOS_USER_FEATURE_ENABLEMENT: "Photos de réalisations",
    QUICK_REPLIES_USER_FEATURE_ENABLEMENT: "Réponses rapides",
    ACTIVITY_PAGE_USER_FEATURE_ENABLEMENT: "Page d'activité",
    DECLARE_JOB_OUTCOME_USER_FEATURE_ENABLEMENT: "Déclarer l'issue d'une demande",
    LEAD_NOTIFICATIONS_USER_FEATURE_ENABLEMENT: "Demandes par notification",
    SMS_LEADS_USER_FEATURE_ENABLEMENT: "Demandes par SMS",
    JOB_REMINDERS_USER_FEATURE_ENABLEMENT: "Rappels de demande",
    SWITCH_MODE_USER_FEATURE_ENABLEMENT: "Passer de client à pro",
    MESSAGING_USER_FEATURE_ENABLEMENT: "Messagerie",
    SEND_PHOTOS_USER_FEATURE_ENABLEMENT: "Envoyer des photos",
    SHARE_LOCATION_USER_FEATURE_ENABLEMENT: "Partager sa position",
    CHANGE_PHONE_USER_FEATURE_ENABLEMENT: "Changer de numéro",
    CHANGE_EMAIL_USER_FEATURE_ENABLEMENT: "Changer d'email",
    CONTACT_US_USER_FEATURE_ENABLEMENT: "Nous contacter",
    CREATE_CLIENT_PROFILE_USER_FEATURE_ENABLEMENT: "Créer un profil client",
    REPORT_USER_USER_FEATURE_ENABLEMENT: "Signaler quelqu'un",
    BLOCK_USER_USER_FEATURE_ENABLEMENT: "Bloquer quelqu'un",
    APPEAL_DECISION_USER_FEATURE_ENABLEMENT: "Contester une décision",
    SECURITY_ACTIVITY_USER_FEATURE_ENABLEMENT: "Activité de sécurité",
    REFER_FRIENDS_USER_FEATURE_ENABLEMENT: "Parrainer des amis",
  },
  /** Every action, so the audit log names what was done instead of its id (#185). */
  actions: {
    SIGN_UP_USER_ACTION: "S'inscrire",
    SIGN_IN_USER_ACTION: "Se connecter",
    SIGN_OUT_USER_ACTION: "Se déconnecter",
    UPDATE_PERSONAL_INFO_USER_ACTION: "Modifier ses informations personnelles",
    CHANGE_PHONE_USER_ACTION: "Changer de numéro de téléphone",
    CHANGE_EMAIL_USER_ACTION: "Changer d'adresse email",
    CHANGE_PASSWORD_USER_ACTION: "Changer de mot de passe",
    SWITCH_MODE_USER_ACTION: "Passer de client à pro",
    CREATE_CLIENT_PROFILE_USER_ACTION: "Créer un profil client",
    UPDATE_SETTING_USER_ACTION: "Modifier un réglage",
    VIEW_SECURITY_ACTIVITY_USER_ACTION: "Voir l'activité de sécurité",
    REFER_FRIEND_USER_ACTION: "Parrainer un ami",
    DELETE_ACCOUNT_USER_ACTION: "Supprimer le compte",
    BECOME_PRO_USER_ACTION: "Devenir pro",
    SUBMIT_VERIFICATION_USER_ACTION: "Envoyer une pièce d'identité pour vérification",
    UPDATE_PRO_PROFILE_USER_ACTION: "Modifier le profil pro",
    UPDATE_RATES_USER_ACTION: "Modifier les tarifs",
    ADD_PORTFOLIO_PHOTO_USER_ACTION: "Ajouter une photo de réalisation",
    REMOVE_PORTFOLIO_PHOTO_USER_ACTION: "Retirer une photo de réalisation",
    UPDATE_LEAD_PREFERENCES_USER_ACTION: "Modifier les préférences d'opportunités",
    PAUSE_LEADS_USER_ACTION: "Mettre les opportunités en pause",
    RESUME_LEADS_USER_ACTION: "Reprendre les opportunités",
    VIEW_OPEN_JOBS_USER_ACTION: "Voir les demandes ouvertes",
    VIEW_ACTIVITY_USER_ACTION: "Voir l'activité",
    REQUEST_PRO_USER_ACTION: "Demander un pro",
    POST_OPEN_JOB_USER_ACTION: "Publier une demande ouverte",
    REBOOK_PRO_USER_ACTION: "Refaire appel à un pro",
    UPDATE_JOB_USER_ACTION: "Modifier une demande",
    ADD_JOB_PHOTOS_USER_ACTION: "Ajouter des photos à une demande",
    SCHEDULE_JOB_USER_ACTION: "Planifier une demande",
    MARK_JOB_URGENT_USER_ACTION: "Marquer une demande urgente",
    SELECT_QUOTE_USER_ACTION: "Choisir un devis",
    CANCEL_JOB_USER_ACTION: "Annuler une demande",
    SUBMIT_QUOTE_USER_ACTION: "Envoyer un devis",
    UPDATE_QUOTE_USER_ACTION: "Modifier un devis",
    WITHDRAW_QUOTE_USER_ACTION: "Retirer un devis",
    ACCEPT_JOB_USER_ACTION: "Accepter une demande",
    DECLINE_JOB_USER_ACTION: "Refuser une demande",
    DECLARE_JOB_OUTCOME_USER_ACTION: "Déclarer l'issue d'une demande",
    SEARCH_PROS_USER_ACTION: "Rechercher des pros",
    SEARCH_PROS_NEARBY_USER_ACTION: "Rechercher des pros à proximité",
    VIEW_PRO_PROFILE_USER_ACTION: "Voir le profil d'un pro",
    SAVE_FAVORITE_PRO_USER_ACTION: "Ajouter un pro aux favoris",
    REMOVE_FAVORITE_PRO_USER_ACTION: "Retirer un pro des favoris",
    SHARE_PRO_PROFILE_USER_ACTION: "Partager le profil d'un pro",
    OPEN_CONVERSATION_USER_ACTION: "Ouvrir une conversation",
    SEND_MESSAGE_USER_ACTION: "Envoyer un message",
    SEND_PHOTO_USER_ACTION: "Envoyer une photo",
    SHARE_LOCATION_USER_ACTION: "Partager une position",
    READ_MESSAGES_USER_ACTION: "Lire les messages",
    SAVE_QUICK_REPLY_USER_ACTION: "Enregistrer une réponse rapide",
    LEAVE_REVIEW_USER_ACTION: "Laisser un avis",
    SUBSCRIBE_TO_PLAN_USER_ACTION: "S'abonner à un forfait",
    CANCEL_SUBSCRIPTION_USER_ACTION: "Annuler le renouvellement",
    RESUME_SUBSCRIPTION_USER_ACTION: "Reprendre le renouvellement",
    CHANGE_PLAN_USER_ACTION: "Changer de forfait",
    PAY_FEE_USER_ACTION: "Payer des frais",
    CONTACT_SUPPORT_USER_ACTION: "Contacter le support",
    REQUEST_CALLBACK_USER_ACTION: "Demander un rappel",
    REPORT_USER_USER_ACTION: "Signaler quelqu'un",
    BLOCK_USER_USER_ACTION: "Bloquer quelqu'un",
    UNBLOCK_USER_USER_ACTION: "Débloquer quelqu'un",
    APPEAL_DECISION_USER_ACTION: "Contester une décision",
    CREATE_EXPERIMENT_STAFF_ACTION: "Lancer une expérience",
    MOVE_EXPERIMENT_STAFF_ACTION: "Étendre ou arrêter une expérience",
    ADD_LEAD_STAFF_ACTION: "Ajouter un prospect",
    MOVE_LEAD_STAFF_ACTION: "Faire avancer un prospect",
    CREATE_CAMPAIGN_STAFF_ACTION: "Préparer une campagne",
    SEND_CAMPAIGN_STAFF_ACTION: "Envoyer une campagne",
    OPEN_INCIDENT_STAFF_ACTION: "Ouvrir un incident",
    UPDATE_INCIDENT_STAFF_ACTION: "Mettre à jour un incident",
    VIEW_MARKETPLACE_STAFF_ACTION: "Voir la santé du marché",
    VIEW_MARKETPLACE_JOB_STAFF_ACTION: "Consulter une demande",
    SIGN_IN_STAFF_ACTION: "Se connecter à la console",
    SIGN_OUT_STAFF_ACTION: "Se déconnecter de la console",
    SET_PASSWORD_STAFF_ACTION: "Définir un mot de passe de console",
    UPDATE_STAFF_SETTING_STAFF_ACTION: "Modifier un réglage de la console",
    SEARCH_ACCOUNTS_STAFF_ACTION: "Rechercher des comptes",
    VIEW_ACCOUNT_STAFF_ACTION: "Voir un compte",
    REVEAL_CONTACT_DETAILS_STAFF_ACTION: "Afficher un numéro de téléphone",
    EDIT_CUSTOMER_SETTING_STAFF_ACTION: "Modifier le réglage d'un client",
    CORRECT_ACCOUNT_DETAILS_STAFF_ACTION: "Corriger les informations d'un compte",
    CLAIM_CASE_STAFF_ACTION: "Prendre un dossier",
    LOG_CALL_STAFF_ACTION: "Noter un appel",
    REPLY_TO_CASE_STAFF_ACTION: "Répondre à un client",
    ADD_CASE_NOTE_STAFF_ACTION: "Ajouter une note interne",
    CLOSE_CASE_STAFF_ACTION: "Clore un dossier",
    REASSIGN_CASE_STAFF_ACTION: "Réattribuer un dossier",
    LINK_CASE_CUSTOMER_STAFF_ACTION: "Lier un client à un dossier",
    START_ON_BEHALF_SESSION_STAFF_ACTION: "Commencer à agir pour le compte d'un client",
    END_ON_BEHALF_SESSION_STAFF_ACTION: "Cesser d'agir pour le compte d'un client",
    VIEW_VERIFICATION_STAFF_ACTION: "Voir une vérification",
    APPROVE_VERIFICATION_STAFF_ACTION: "Approuver une vérification",
    REJECT_VERIFICATION_STAFF_ACTION: "Refuser une vérification",
    VIEW_ID_IMAGES_STAFF_ACTION: "Voir les images d'identité",
    APPROVE_PRO_STAFF_ACTION: "Approuver un pro",
    REJECT_PRO_STAFF_ACTION: "Refuser un pro",
    VIEW_PAYMENTS_STAFF_ACTION: "Voir les paiements",
    ISSUE_REFUND_STAFF_ACTION: "Émettre un remboursement",
    ISSUE_CREDIT_STAFF_ACTION: "Émettre un crédit",
    WAIVE_FEE_STAFF_ACTION: "Annuler des frais",
    MARK_PAYMENT_PAID_STAFF_ACTION: "Marquer des frais payés",
    APPROVE_PAYMENT_REQUEST_STAFF_ACTION: "Accorder une demande",
    REJECT_PAYMENT_REQUEST_STAFF_ACTION: "Refuser une demande",
    SETTLE_REFUND_STAFF_ACTION: "Régler un remboursement",
    SET_PLAN_PRICE_STAFF_ACTION: "Fixer le prix d'un forfait",
    CREATE_PROMO_STAFF_ACTION: "Créer une promo",
    END_PROMO_STAFF_ACTION: "Terminer une promo",
    CREATE_DISCOUNT_STAFF_ACTION: "Créer une remise",
    END_DISCOUNT_STAFF_ACTION: "Terminer une remise",
    EXTEND_SUBSCRIPTION_STAFF_ACTION: "Prolonger un abonnement",
    END_SUBSCRIPTION_STAFF_ACTION: "Terminer un abonnement",
    RECONCILE_PAYMENTS_STAFF_ACTION: "Rapprocher les paiements",
    EXPORT_PAYMENTS_STAFF_ACTION: "Exporter les paiements",
    OPEN_SAFETY_REPORT_STAFF_ACTION: "Ouvrir un signalement",
    ADD_REPORT_EVIDENCE_STAFF_ACTION: "Ajouter une preuve à un signalement",
    SET_REPORT_SEVERITY_STAFF_ACTION: "Fixer la gravité d'un signalement",
    SUSPEND_PENDING_INVESTIGATION_STAFF_ACTION: "Suspendre un compte pendant l'enquête",
    LIFT_SUSPENSION_STAFF_ACTION: "Lever une suspension",
    ISSUE_VERDICT_STAFF_ACTION: "Rendre un verdict",
    BAN_PERMANENTLY_STAFF_ACTION: "Bannir définitivement un compte",
    DECIDE_APPEAL_STAFF_ACTION: "Trancher un recours",
    REFER_TO_POLICE_STAFF_ACTION: "Transmettre un dossier à la police",
    SHARE_DATA_WITH_POLICE_STAFF_ACTION: "Partager des données avec la police",
    PLACE_LEGAL_HOLD_STAFF_ACTION: "Poser un gel légal",
    RELEASE_LEGAL_HOLD_STAFF_ACTION: "Lever un gel légal",
    HIDE_REVIEW_STAFF_ACTION: "Masquer un avis",
    RESTORE_REVIEW_STAFF_ACTION: "Rétablir un avis",
    SEND_SMS_STAFF_ACTION: "Envoyer un SMS",
    SEND_BULK_SMS_STAFF_ACTION: "Envoyer un SMS à plusieurs personnes",
    EDIT_SMS_TEMPLATE_STAFF_ACTION: "Modifier un modèle de SMS",
    SET_USER_FEATURE_ENABLEMENT_STAFF_ACTION: "Activer ou désactiver une fonctionnalité client",
    CREATE_RULE_VERSION_STAFF_ACTION: "Modifier une règle de l'entreprise",
    EDIT_PRICING_STAFF_ACTION: "Modifier les paliers de frais",
    EDIT_CATEGORIES_STAFF_ACTION: "Modifier les métiers",
    INVITE_STAFF_STAFF_ACTION: "Inviter un membre de l'équipe",
    DEACTIVATE_STAFF_STAFF_ACTION: "Désactiver un membre de l'équipe",
    REACTIVATE_STAFF_STAFF_ACTION: "Réactiver un membre de l'équipe",
    END_STAFF_SESSION_STAFF_ACTION: "Couper les sessions d'un membre de l'équipe",
    REQUEST_PERMISSION_STAFF_ACTION: "Demander une permission",
    DECIDE_PERMISSION_REQUEST_STAFF_ACTION: "Répondre à une demande de permission",
    UPDATE_ACTION_ENABLEMENT_STAFF_ACTION: "Modifier l'activation d'une action",
    REQUEST_TIME_OFF_STAFF_ACTION: "Demander un congé",
    DECIDE_TIME_OFF_STAFF_ACTION: "Décider d'une demande de congé",
    SET_EMPLOYMENT_STAFF_ACTION: "Modifier un dossier d'emploi",
    OPEN_JOB_STAFF_ACTION: "Ouvrir un poste",
    EDIT_JOB_STAFF_ACTION: "Modifier un poste",
    MOVE_CANDIDATE_STAFF_ACTION: "Faire avancer un candidat",
    OPEN_PAYROLL_STAFF_ACTION: "Ouvrir un mois de paie",
    EDIT_PAYROLL_LINE_STAFF_ACTION: "Suspendre ou libérer une ligne de paie",
    CLOSE_PAYROLL_STAFF_ACTION: "Clôturer un mois de paie",
    OPEN_REVIEW_CYCLE_STAFF_ACTION: "Ouvrir un cycle d'évaluation",
    WRITE_REVIEW_STAFF_ACTION: "Rédiger une évaluation",
    EDIT_COURSE_STAFF_ACTION: "Ajouter ou modifier une formation",
    ENROL_IN_COURSE_STAFF_ACTION: "S'inscrire à une formation",
    CREATE_TEAM_STAFF_ACTION: "Créer une équipe",
    EDIT_TEAM_STAFF_ACTION: "Modifier une équipe",
    SET_TEAM_MEMBERSHIP_STAFF_ACTION: "Définir les membres d'une équipe",
    CREATE_ROLE_STAFF_ACTION: "Créer un rôle",
    EDIT_ROLE_STAFF_ACTION: "Modifier un rôle",
    SET_STAFF_PERMISSION_STAFF_ACTION: "Accorder ou retirer une permission",
    SET_STAFF_FEATURE_ENABLEMENT_STAFF_ACTION: "Accorder ou retirer une fonctionnalité",
    ASSIGN_ROLE_STAFF_ACTION: "Attribuer un rôle",
    REMOVE_ROLE_STAFF_ACTION: "Retirer un rôle",
    VIEW_AUDIT_LOG_STAFF_ACTION: "Lire le journal d'audit",
    VIEW_SENSITIVE_AUDIT_VALUES_STAFF_ACTION: "Voir les valeurs sensibles de l'audit",
    VIEW_HEALTH_DASHBOARD_STAFF_ACTION: "Voir le tableau de bord de santé",
    EXPIRE_JOB_SYSTEM_ACTION: "Faire expirer une demande sans réponse",
    EXPIRE_QUOTE_SYSTEM_ACTION: "Faire expirer un devis",
    CLOSE_OTHER_QUOTES_SYSTEM_ACTION: "Clore les autres devis",
    OPEN_CONVERSATION_ON_QUOTE_SYSTEM_ACTION: "Ouvrir une conversation sur un devis",
    NOTIFY_USER_SYSTEM_ACTION: "Envoyer une notification",
    NOTIFY_NEW_LEAD_SYSTEM_ACTION: "Prévenir un pro d'une nouvelle opportunité",
    SEND_LEAD_SMS_SYSTEM_ACTION: "Envoyer une opportunité par SMS",
    NOTIFY_JOB_REMINDER_SYSTEM_ACTION: "Envoyer un rappel de demande",
    CONFIRM_FEE_PAYMENT_SYSTEM_ACTION: "Confirmer un paiement de frais",
    FAIL_FEE_PAYMENT_SYSTEM_ACTION: "Noter un paiement de frais échoué",
    ACTIVATE_SUBSCRIPTION_SYSTEM_ACTION: "Activer un abonnement",
    SUSPEND_SUBSCRIPTION_SYSTEM_ACTION: "Suspendre un abonnement",
    RENEW_SUBSCRIPTION_SYSTEM_ACTION: "Renouveler un abonnement",
    EXPIRE_SUBSCRIPTION_SYSTEM_ACTION: "Terminer un abonnement échu",
    REMIND_SUBSCRIPTION_EXPIRY_SYSTEM_ACTION: "Rappeler à un pro que son forfait se termine",
    EXPIRE_SETTING_VALUE_SYSTEM_ACTION: "Faire expirer la valeur d'un réglage",
    DELETE_ID_IMAGES_SYSTEM_ACTION: "Supprimer les images d'identité",
    FLAG_BANNED_DOCUMENT_SYSTEM_ACTION: "Signaler un document interdit",
    SUSPEND_ON_CRITICAL_REPORT_SYSTEM_ACTION: "Suspendre sur signalement critique",
  },
  /** The settings someone chooses for themselves, read as words, not ids (#185). */
  settings: {
    THEME_USER_SETTING: "Thème",
    COLOR_VIBE_USER_SETTING: "Couleur",
    LANGUAGE_USER_SETTING: "Langue",
    LOW_DATA_MODE_USER_SETTING: "Mode économie de données",
    NEW_LEAD_NOTIFICATION_USER_SETTING: "Me prévenir des nouvelles opportunités",
    QUOTE_RECEIVED_NOTIFICATION_USER_SETTING: "Me prévenir quand un devis arrive",
    NEW_LEAD_SMS_USER_SETTING: "Envoyer les nouvelles opportunités par SMS",
    QUOTE_RECEIVED_SMS_USER_SETTING: "Envoyer un devis par SMS",
    LEAD_TRADES_USER_SETTING: "Métiers pour lesquels je veux des opportunités",
    LEAD_SERVICE_AREA_USER_SETTING: "Où je travaille",
    LEAD_RADIUS_USER_SETTING: "Jusqu'où je me déplace",
    LEAD_DAYS_USER_SETTING: "Jours où je prends du travail",
    URGENT_JOBS_USER_SETTING: "Demandes urgentes",
    CONSOLE_THEME_STAFF_SETTING: "Thème de la console",
    CONSOLE_LANGUAGE_STAFF_SETTING: "Langue de la console",
  },
  /** The grid's section titles. */
  categories: {
    appearance: "Apparence",
    notifications: "Notifications",
    leads: "Opportunités",
    discovery: "Recherche",
    jobs: "Demandes",
    profile: "Profil",
    accounts: "Comptes",
    support: "Dossiers",
    onBehalf: "Pour le compte d'un client",
    onboarding: "Vérification",
    payments: "Paiements",
    safety: "Confiance & sécurité",
    messaging: "Messages",
    configuration: "Configuration",
    staff: "Équipe & accès",
    audit: "Audit",
  },
};
